/**
 * WizardStepScopes (Step 3)
 *
 * Scope discovery - for each selected trade, ask what specific work.
 * Users can select multiple scopes per trade (e.g., panel upgrade AND new circuits).
 * Options are loaded from job_scopes table filtered by trade.
 */

import { useCallback } from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { useJobScopesForTrades } from '@/hooks/useWizardData';
import type { WizardScopesData, WizardTradesData, SelectedScope } from '@/types/wizard';

interface WizardStepScopesProps {
  data: WizardScopesData;
  onUpdate: (data: Partial<WizardScopesData>) => void;
  tradesData: WizardTradesData;
}

export function WizardStepScopes({ data, onUpdate, tradesData }: WizardStepScopesProps) {
  const { data: jobScopesMap, isLoading } = useJobScopesForTrades(tradesData.selectedTradeIds);

  // Handle scope toggle for a trade
  const handleScopeToggle = useCallback((
    tradeId: number,
    tradeCode: string,
    tradeName: string,
    scopeId: number,
    scopeCode: string,
    scopeName: string,
    checked: boolean
  ) => {
    // Find existing trade scope entry or create new one
    const existingTradeScope = data.tradeScopes.find((ts) => ts.tradeId === tradeId);

    let newTradeScopes;

    if (existingTradeScope) {
      // Update existing trade scope
      newTradeScopes = data.tradeScopes.map((ts) => {
        if (ts.tradeId !== tradeId) return ts;

        let newSelectedScopes: SelectedScope[];
        // Safety check for old data format where selectedScopes may be undefined
        const currentScopes = ts.selectedScopes || [];
        if (checked) {
          // Add scope
          newSelectedScopes = [...currentScopes, { scopeId, scopeCode, scopeName }];
        } else {
          // Remove scope
          newSelectedScopes = currentScopes.filter((s) => s.scopeId !== scopeId);
        }

        return {
          ...ts,
          selectedScopes: newSelectedScopes,
        };
      });
    } else {
      // Create new trade scope entry
      newTradeScopes = [
        ...data.tradeScopes,
        {
          tradeId,
          tradeCode,
          tradeName,
          selectedScopes: checked ? [{ scopeId, scopeCode, scopeName }] : [],
        },
      ];
    }

    onUpdate({ tradeScopes: newTradeScopes });
  }, [data.tradeScopes, onUpdate]);

  // Get selected scopes for a trade
  const getSelectedScopes = (tradeId: number): SelectedScope[] => {
    return data.tradeScopes.find((ts) => ts.tradeId === tradeId)?.selectedScopes || [];
  };

  // Check if a scope is selected
  const isScopeSelected = (tradeId: number, scopeId: number): boolean => {
    const selectedScopes = getSelectedScopes(tradeId);
    return selectedScopes.some((s) => s.scopeId === scopeId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (tradesData.trades.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">
          Please go back and select at least one trade first.
        </p>
      </div>
    );
  }

  // Count total selected scopes across all trades (with safety check for old data format)
  const totalSelectedScopes = data.tradeScopes.reduce(
    (sum, ts) => sum + (ts.selectedScopes?.length || 0),
    0
  );

  return (
    <div className="space-y-8">
      {tradesData.trades.map((trade) => {
        const scopes = jobScopesMap?.get(trade.id) || [];
        const selectedScopes = getSelectedScopes(trade.id);

        return (
          <Card key={trade.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{trade.name}</CardTitle>
                {selectedScopes.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {selectedScopes.length} selected
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                What type of {trade.name.toLowerCase()} work? Select all that apply.
              </p>
            </CardHeader>
            <CardContent>
              {scopes.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No specific scopes defined for this trade. You can continue with a general permit.
                </p>
              ) : (
                <div className="grid gap-2">
                  {scopes.map((scope) => {
                    const isSelected = isScopeSelected(trade.id, scope.id);

                    return (
                      <Label
                        key={scope.id}
                        htmlFor={`scope-${trade.id}-${scope.id}`}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all hover:bg-muted/50 ${
                          isSelected ? 'border-primary bg-primary/5 ring-1 ring-primary' : ''
                        }`}
                      >
                        <Checkbox
                          id={`scope-${trade.id}-${scope.id}`}
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            handleScopeToggle(
                              trade.id,
                              trade.code,
                              trade.name,
                              scope.id,
                              scope.code,
                              scope.name,
                              checked === true
                            );
                          }}
                          className="mt-0.5 h-5 w-5 rounded-[3px] border-2"
                        />
                        <div className="flex-1">
                          <span className="font-medium">{scope.name}</span>
                          {scope.description && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {scope.description}
                            </p>
                          )}
                        </div>
                      </Label>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Summary */}
      {totalSelectedScopes > 0 && (
        <div className="p-4 bg-muted rounded-lg">
          <p className="text-sm font-medium mb-2">
            {totalSelectedScopes} scope{totalSelectedScopes !== 1 ? 's' : ''} selected
          </p>
          <div className="space-y-2">
            {data.tradeScopes
              .filter((ts) => (ts.selectedScopes?.length || 0) > 0)
              .map((ts) => (
                <div key={ts.tradeId}>
                  <span className="text-sm text-muted-foreground">{ts.tradeName}:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {ts.selectedScopes.map((scope) => (
                      <Badge key={scope.scopeId} variant="secondary" className="text-xs">
                        {scope.scopeName}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {totalSelectedScopes === 0 && (
        <p className="text-sm text-amber-600 dark:text-amber-500">
          Please select at least one scope to continue, or proceed with general permits.
        </p>
      )}
    </div>
  );
}
